"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Snowflake, ShieldCheck, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const features = [{
  icon: Snowflake,
  title: "Trailer Sizes",
  description: "Choose from 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a 53-foot reefer trailer. Availability and requirements are confirmed individually."
}, {
  icon: Clock,
  title: "Service Area",
  description: "Serving our listed Southern California and Las Vegas-area communities"
}, {
  icon: ShieldCheck,
  title: "Delivery and Setup",
  description: "Delivery, positioning, setup, and pickup arrangements are confirmed with each quote. Requirements for the 53-foot reefer trailer are reviewed individually."
}];
export default function Cta() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="call-to-action" data-nav="light" className="relative bg-background py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="relative rounded-2xl bg-card border border-border p-6 sm:p-12 md:p-16 shadow-lg text-center">
          {/* Subtle top indicator pill */}
          <motion.div variants={itemVariants} className="inline-flex items-center justify-center mb-6 max-w-full">
            <div className="inline-flex items-center gap-2 rounded-full border border-accent-foreground/20 bg-accent px-3.5 py-1.5 text-xs font-semibold text-accent-foreground max-w-full">
              <span className="size-2 rounded-full bg-primary shrink-0 animate-pulse" />
              <span className="truncate">Mobile Cold Storage Rentals</span>
            </div>
          </motion.div>

          {/* Heading */}
          <motion.h2 variants={itemVariants} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl max-w-3xl mx-auto break-words">Need Mobile Cold Storage for Your Business or Event?</motion.h2>

          {/* Supporting Subtext */}
          <motion.p variants={itemVariants} className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed break-words">Tell us your requested dates, delivery location, intended use, temperature requirements, preferred trailer size, site-access details, and available power so we can prepare a quote.</motion.p>

          {/* Action Buttons */}
          <motion.div variants={itemVariants} className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
            <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-colors">
              <Link to="/ContactRequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                <span>Request a Quote</span>
                <ArrowRight className="size-4 shrink-0" />
              </Link>
            </Button>

            <a href="tel:2135218821" className="w-full sm:w-auto h-12 px-7 inline-flex items-center justify-center gap-2 text-base font-semibold bg-secondary text-secondary-foreground rounded-md shadow-sm">
              <span>213-521-8821</span>
            </a>
          </motion.div>

          {/* Service Guarantee Grid */}
          <motion.div variants={itemVariants} className="mt-12 pt-10 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
            {features.map((feature, i) => {
            const IconComp = feature.icon;
            return <div key={feature.title} data-index={i} className="flex items-start gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
                    <IconComp className="size-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-default text-sm font-semibold text-foreground break-words">
                      {feature.title}
                    </h3>
                    <p className="mt-0.5 text-xs text-muted-foreground break-words">
                      {feature.title === "Trailer Sizes" ? <>
                          Choose from 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a{" "}
                          <Link to="/FootRefrigeratedTrailerRentalsPage" className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                            53-foot reefer trailer
                          </Link>
                          . Availability and requirements are confirmed individually.
                        </> : feature.description}
                    </p>
                  </div>
                </div>;
          })}
          </motion.div>
        </motion.div>
      </div>
    </section>;
}